import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import GalleryGrid from '../components/GalleryGrid';

export default function Consulting() {
  const consultingServices = [
    {
      title: 'Mekanisk konstruktion',
      path: '/ydelser/mekanisk-konstruktion',
      desc: 'Udvikling og konstruktion af maskiner og maskindele samt freelance konstruktion. Professionel projektbistand tilpasset din virksomhed.',
      icon: 'fa-solid fa-gears',
      features: ['Udvikling og konstruktion af maskiner', 'Maskindele og komponenter', 'Freelance konstruktion', 'Projektarbejde'],
    },
    {
      title: 'Elkonstruktion',
      path: '/ydelser/elkonstruktion',
      desc: 'Konstruktion og opbygning af styretavler efter gældende standarder, eldiagrammer i PC Schematik samt CE mærkning.',
      icon: 'fa-solid fa-bolt',
      features: ['Styretavler efter standarder', 'Eldiagrammer (PC Schematik)', 'Sprog: Dansk, Tysk og Engelsk', 'CE mærkning'],
    },
    {
      title: 'CAD design og FEM',
      path: '/ydelser/cad-fem',
      desc: '2D/3D tegning og Finite Elemente Analyse. Vi tegner primært i Autodesk Inventor, men kan også tegne i Siemens NX og PTC Creo.',
      icon: 'fa-solid fa-drafting-compass',
      features: ['2D/3D teknisk tegning', 'Finite Elemente Analyse (FEM)', 'Autodesk Inventor', 'Siemens NX og PTC Creo'],
    },
    {
      title: 'Montage og indkøring',
      path: '/ydelser/montage-indkoring',
      desc: 'Montage og indkøring af maskiner og delkomponenter mekanisk og elektrisk. Praktisk erfaring sikrer problemfri opstart.',
      icon: 'fa-solid fa-screwdriver-wrench',
      features: ['Mekanisk montage', 'Elektrisk indkøring', 'Test og idriftsættelse', 'Fejlretning'],
    },
    {
      title: 'Dokumentation og CE mærkning',
      path: '/ydelser/dokumentation-ce',
      desc: 'Udfærdige mekanisk og elektrisk dokumentation (PC Schematik) på Dansk, Tysk og Engelsk. CE mærkning i overensstemmelse med lovkrav.',
      icon: 'fa-solid fa-file-shield',
      features: ['PC Schematik dokumentation', 'Flersproget (DA, DE, EN)', 'Maskindirektivet', 'Fuld CE mærkning'],
    },
  ];

  const galleryImages = [
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/8a3e6d-38f93c76eb184f888d48b892497ae3dc.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/mekanisk-konstruktion-cad.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/elkonstruktion-styretavle.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-f13a7513b1d94a9ca64d009f1172fcf7-mv2.jpg',
  ];

  return (
    <div className="bg-[#0b1017] text-slate-100 min-h-screen">
      <SEO
        title="Konsulentydelser - JARCon ApS"
        description="Konsulentydelser inden for udvikling, konstruktion, test, dokumentation m.m. JARCon ApS tilbyder stærk ingeniørrådgivning."
      />

      <PageHeader
        badge="Ingeniørrådgivning"
        title="Konsulentydelser"
        subtitle="Udvikling, konstruktion, test, dokumentation m.m. JARCon ApS tilbyder ydelser over et bredt område."
        breadcrumbs={[{ label: 'Konsulentydelser' }]}
        ctaText="Få en teknisk vurdering"
        ctaLink="/kontakt"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Intro text from scrape */}
        <div className="max-w-3xl mb-16 p-8 rounded-xl bg-[#0e1622] border border-slate-800">
          <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest block mb-2 font-semibold">
            Mekatronik kompetence
          </span>
          <h2 className="font-display text-2xl font-bold text-white mb-4">
            JARCon ApS tilbyder ydelser over et bredt område:
          </h2>
          <p className="text-slate-300 leading-relaxed text-base">
            Mekatronik omfatter mekanik, elektronik og software/programmering. Med en stærk praktisk baggrund kombineret med en diplomuddannelse i mekatronik yder vi teknisk konsulentbistand, freelance konstruktion og opbygning af produktionsudstyr.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {consultingServices.map((svc) => (
            <div
              key={svc.title}
              className="flex flex-col justify-between p-8 rounded-xl bg-[#0e1622] border border-slate-800 hover:border-cyan-500/50 transition-all duration-200"
            >
              <div>
                <div className="w-12 h-12 rounded-lg bg-cyan-950/70 border border-cyan-500/30 text-cyan-400 flex items-center justify-center text-xl mb-6">
                  <i className={svc.icon} aria-hidden="true" />
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-3">
                  {svc.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-6 font-sans">
                  {svc.desc}
                </p>

                <ul className="space-y-2 mb-6 text-xs text-slate-300 font-open-sans">
                  {svc.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-cyan-400 text-[10px]" aria-hidden="true" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to={svc.path}
                className="inline-flex items-center justify-between w-full pt-4 border-t border-slate-800 text-xs font-open-sans text-cyan-400 hover:text-cyan-300 font-semibold group"
              >
                <span>Gå til undersiden for {svc.title}</span>
                <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          ))}
        </div>

        {/* Gallery */}
        <GalleryGrid
          title="Billeder fra konsulent- og konstruktionsarbejde"
          subtitle="Tekniske tegninger, CAD, tavler og maskinkomponenter"
          images={galleryImages}
        />
      </div>
    </div>
  );
}
